<script setup lang="ts">
import { LoaderCircle } from '@lucide/vue'
import { cva, type VariantProps } from 'class-variance-authority'
import type { ButtonHTMLAttributes, Component } from 'vue'

import { cn } from '@/shared/lib/cn'

defineOptions({ inheritAttrs: false })

const buttonVariants = cva(
  'inline-flex shrink-0 cursor-pointer items-center justify-center rounded-md border border-transparent bg-clip-padding text-sm font-medium whitespace-nowrap transition-all outline-none select-none focus-visible:border-(--ring) focus-visible:ring-3 focus-visible:ring-(--focus-ring) disabled:pointer-events-none disabled:opacity-50 active:not-aria-[haspopup]:translate-y-px',
  {
    variants: {
      variant: {
        primary: 'bg-accent text-accent-foreground hover:bg-accent/80',
        secondary:
          'border-border bg-background text-foreground shadow-xs hover:bg-surface-subtle dark:border-input dark:bg-input/30 dark:hover:bg-input/50',
        ghost:
          'bg-transparent text-foreground hover:bg-surface-subtle dark:hover:bg-surface-subtle/50',
        danger:
          'bg-danger/10 text-danger hover:bg-danger/20 dark:bg-danger/20 dark:hover:bg-danger/30',
      },
      size: {
        sm: 'h-8 gap-1 px-2.5',
        md: 'h-9 gap-1.5 px-2.5',
        lg: 'h-10 gap-1.5 px-2.5',
        icon: 'size-9 p-0',
      },
      block: {
        true: 'w-full',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'secondary',
      size: 'md',
      block: false,
    },
  },
)

type ButtonVariants = VariantProps<typeof buttonVariants>

const props = withDefaults(
  defineProps<{
    variant?: ButtonVariants['variant']
    size?: ButtonVariants['size']
    block?: boolean
    icon?: Component
    loadingIcon?: Component
    iconSize?: number
    loading?: boolean
    disabled?: boolean
    type?: ButtonHTMLAttributes['type']
    label?: string
  }>(),
  {
    variant: 'secondary',
    size: 'md',
    block: false,
    icon: undefined,
    loadingIcon: undefined,
    iconSize: 15,
    loading: false,
    disabled: false,
    type: 'button',
    label: undefined,
  },
)
</script>

<template>
  <button
    v-bind="$attrs"
    :type="props.type"
    :disabled="props.disabled || props.loading"
    :aria-busy="props.loading || undefined"
    :aria-label="props.label"
    :class="cn(buttonVariants({ variant, size, block }), $attrs.class as string)"
  >
    <component
      :is="props.loading ? (props.loadingIcon ?? LoaderCircle) : props.icon"
      v-if="props.icon || props.loading"
      :size="props.iconSize"
      :class="{ 'animate-spin': props.loading }"
      aria-hidden="true"
    />
    <slot />
  </button>
</template>
